import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DashboardContext from "../context/DashboardContext";
import InputMask from "react-input-mask";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Container from "@mui/material/Container";
import CustomButton from "../components/CustomButton";
import CustomContainer from "../components/CustomContainer";
import CustomTextField from "../components/CustomTextField";
import EditIcon from "@mui/icons-material/Edit";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import { Autocomplete, Checkbox, FormControlLabel, InputAdornment } from "@mui/material";
import { cpfSpecialCharactersRemover } from "../utils";
import { getProducts, getPlans } from "../services/SubmitEstablishment";
import { setNewMember } from "../services";
import { Opacity } from "@mui/icons-material";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (dateRegex.test(dateString)) {
    return dateString;
  }
  const [day, month, year] = dateString.split('/');
  return `${year}-${month}-${day}`;
};

const revertFormatDate = (dateString) => {
  if (!dateString) return "";
  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (dateRegex.test(dateString)) {
    return dateString;
  }
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

function cleanPhoneNumber(phoneNumber) {
  if (!phoneNumber) return "";
  return phoneNumber.replace(/[^\d]/g, '');
}

export default function CreateOrEditMember({ edit }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient(); // Inicializando o queryClient

  const { editMember, nodeToken, clientId } = useContext(DashboardContext);
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(edit ? editMember.products : []);
  const [isCnpj, setIsCnpj] = useState(false);
  const INITIAL_INPUTS_VALUE = {
    name: edit ? editMember.name : "",
    cpf: edit ? editMember.cpf : "",
    email: edit ? editMember.email : "",
    phone: edit ? editMember.phone : "",
    birthDate: edit ? revertFormatDate(editMember.birthDate) : "",
    gender: edit ? editMember.gender : "",
    plan: edit ? editMember.telemedicinePlan : "",
    address: edit ? editMember.address : "",  // Novo campo
    addressNum: edit ? editMember.addressNum : "",     // Novo campo
  };
  const [formInputs, setFormInputs] = useState(INITIAL_INPUTS_VALUE);

  async function pushProducts() {
    try {
      const data = await getProducts(nodeToken);
      setProducts(data);
    } catch (err) {
      console.log(err);
    }
  }
  async function pushPlans() {
    try {
      const data = await getPlans(nodeToken);
      setPlans(data);
    } catch (err) {
      console.log(err);
    }
  }
  useEffect(() => {
    if (!products?.length && nodeToken) {
      pushProducts();
    }
    if (!plans?.length && nodeToken) {
      pushPlans();
    }
  }, [nodeToken, products, plans]);

  const mutation = useMutation(
    async (updatedFormInputs) => {
      return await setNewMember({
        token: nodeToken,
        member: updatedFormInputs,
      });
    },
    {
      onSuccess: () => {
        // Invalida a query 'members' para garantir que a lista de membros seja atualizada
        queryClient.invalidateQueries("members");
        toast.success(edit ? "Associado editado com sucesso!" : "Associado cadastrado com sucesso!");
        setFormInputs(INITIAL_INPUTS_VALUE);
        navigate("/members");
      },
      onError: (err) => {
        console.log(err);
        toast.error(err.title);
      },
    }
  );

  const handleTextInputsChange = ({ target: { name, value } }) => {
    setFormInputs((prevState) => ({
      ...prevState,
      [name]: name === "cpf" ? cpfSpecialCharactersRemover(value) : value,
    }));
  };

  const NUM_OF_04TELEMED_ID = 4;
  const NUM_OF_14TELEMED_ID = 14;
  const REQUIRED_PRODUCT_IDS = [3, 4, 14];

  const isProductRequired = () => {
    return selectedProducts.some((product) =>
      REQUIRED_PRODUCT_IDS.includes(product.id)
    );
  };

  const isTelemedRequired = () => {
    return selectedProducts.some((product) =>
      product.id === NUM_OF_04TELEMED_ID || product.id === NUM_OF_14TELEMED_ID
    );
  };

  const removeEmptyNonRequiredFields = (inputs) => {
    const updatedInputs = { ...inputs };
    const optionalFields = ["email", "phone", "birthDate", "gender", "plan"];

    optionalFields.forEach((field) => {
      if (!updatedInputs[field]) {
        delete updatedInputs[field];
      }
    });

    if (!isTelemedRequired) {
      delete updatedInputs["plan"];
    }

    return updatedInputs;
  };

  const submitUpdateMember = async (event) => {
    event.preventDefault();
    if (mutation.isLoading) {
      return;
    }
    if (!selectedProducts.length) {
      toast.info("Selecione ao menos um produto!");
      return;
    }

    let updatedFormInputs = {
      ...formInputs,
      birthDate: formatDate(formInputs.birthDate),
      phone: cleanPhoneNumber(formInputs.phone),
    };

    selectedProducts.forEach((product) => {
      const { key } = product;
      updatedFormInputs[key] = true;
    });

    updatedFormInputs = removeEmptyNonRequiredFields(updatedFormInputs);

    mutation.mutate(updatedFormInputs);
  };

  return (
    <Container>
      <CustomContainer
        style={{ padding: "32px 48px" }}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 1100,
        }}
      >
        <Stack
          direction="row"
          spacing={3}
          py={2}
          sx={{
            justifyContent: "center",
            width: "60%",
            margin: "auto",
          }}
        >
          <Typography
            color="primary"
            component="h2"
            mb={4}
            pl={5}
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              width: "500px",
              paddingleft: "200px",
            }}
            variant="h4"
          >
            <EditIcon fontSize="md" sx={{ mr: 1 }} />
            {edit ? "Editar Associado" : "Cadastrar Associado"}
          </Typography>
        </Stack>

        <form onSubmit={submitUpdateMember}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <CustomTextField
                autoComplete="off"
                color="primary"
                label="Nome"
                name="name"
                onChange={handleTextInputsChange}
                required
                size="small"
                sx={{ width: "50%" }}
                type="text"
                value={formInputs.name}
                variant="outlined"
              />
              <InputMask
                mask={isCnpj ? "99.999.999/9999-99" : "999.999.999-99"}
                disabled={edit}
                maskChar=" "
                onChange={handleTextInputsChange}
                required={!edit}
                value={formInputs.cpf}
              >
                {() => (
                  <CustomTextField
                    name="cpf"
                    autoComplete="off"
                    disabled={edit}
                    color="primary"
                    label={isCnpj ? "CNPJ *" : "CPF *"}
                    size="small"
                    sx={{ width: "50%" }}
                    variant="outlined"
                    type="text"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isCnpj}
                                disabled={edit}
                                onChange={(e) => setIsCnpj(e.target.checked)}
                                color="primary"
                              />
                            }
                            label="CNPJ"
                            labelPlacement="end"
                            sx={{ m: 0 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </InputMask>
            </Stack>
            <Stack direction="row" spacing={2}>
              <CustomTextField
                autoComplete="off"
                color="primary"
                label="E-mail"
                name="email"
                onChange={handleTextInputsChange}
                required={isProductRequired()}
                size="small"
                sx={{ width: "50%" }}
                type="email"
                value={formInputs.email}
                variant="outlined"
              />

              <CustomTextField
                autoComplete="off"
                color="primary"
                label="Telefone"
                name="phone"
                onChange={handleTextInputsChange}
                required={isProductRequired()}
                size="small"
                sx={{ width: "50%" }}
                type="text"
                value={formInputs.phone}
                variant="outlined"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Autocomplete
                onChange={(event, value) => {
                  setSelectedProducts(value);
                }}
                options={products}
                value={selectedProducts}
                sx={{ width: "100%" }}
                getOptionLabel={(option) => `${option.id} - ${option.name}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                multiple
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    color="primary"
                    label="Produtos"
                    name="product"
                    size="small"
                    variant="outlined"
                  />
                )}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <InputMask
                mask="99/99/9999"
                disabled={false}
                maskChar=" "
                onChange={handleTextInputsChange}
                required={isProductRequired()}
                value={formInputs.birthDate}
              >
                {() => (
                  <CustomTextField
                    name="birthDate"
                    autoComplete="off"
                    color="primary"
                    label="Nascimento"
                    size="small"
                    sx={{ width: "50%" }}
                    variant="outlined"
                    type="text"
                  />
                )}
              </InputMask>

              <CustomTextField
                autoComplete="off"
                color="primary"
                label="Gênero"
                name="gender"
                onChange={handleTextInputsChange}
                required={isProductRequired()}
                size="small"
                sx={{ width: "50%" }}
                select
                value={formInputs.gender}
                variant="outlined"
              >
                <MenuItem value="M">M</MenuItem>
                <MenuItem value="F">F</MenuItem>
              </CustomTextField>

              {selectedProducts.some((product) => product.id === NUM_OF_04TELEMED_ID || product.id === NUM_OF_14TELEMED_ID) &&
                <CustomTextField
                  autoComplete="off"
                  color="primary"
                  label={plans.length > 0 ? "Plano-Telemedicina" : "Sem Planos Cadastrados"}
                  name="plan"
                  onChange={handleTextInputsChange}
                  required
                  disabled={plans.length > 0 ? false : true}
                  size="small"
                  sx={{ width: "50%" }}
                  select
                  value={formInputs.plan}
                  variant="outlined"
                >
                  {plans.length > 0 && plans.map((plan) => (
                    <MenuItem key={`telemedPlans-${plan}`} value={plan}>{plan}</MenuItem>
                  ))}
                </CustomTextField>
              }
            </Stack>
            
            {
              clientId == "2546" && (
                <Stack direction="row" spacing={2}>
                  <CustomTextField
                    autoComplete="off"
                    color="primary"
                    label="Condomínio"
                    name="address"
                    onChange={handleTextInputsChange}
                    size="small"
                    sx={{ width: "50%" }}
                    type="text"
                    value={formInputs.address}
                    variant="outlined"
                  />
                  <CustomTextField
                    autoComplete="off"
                    color="primary"
                    label="Apartamento"
                    name="addressNum"
                    onChange={handleTextInputsChange}
                    size="small"
                    sx={{ width: "50%" }}
                    type="text"
                    value={formInputs.addressNum}
                    variant="outlined"
                  />
                </Stack>
              )
            }
            

            <Box
              sx={{ display: "flex", justifyContent: "center", width: "100%" }}
            >
              <CustomButton
                sx={{ width: "200px" }}
                type="submit"
                variant="contained"
                disabled={mutation.isLoading}
              >
                {mutation.isLoading ? "Carregando..." : (edit ? "Salvar Alterações" : "Cadastrar")}
              </CustomButton>
            </Box>
          </Stack>
        </form>
      </CustomContainer>
    </Container>
  );
}
